import { HttpClient, robotoHeaders, RobotoNotFoundException } from "../../http";

import {
  RobotoUserConfigKey,
  TableSearchPreferencesConfig,
  TableUIPreferences,
} from "./ConfigRecords";

interface Options {
  abortSignal: AbortSignal;
}

interface GetConfigResponse<T = unknown> {
  key: string;
  value: T;
}

interface SetConfigRequest {
  value: unknown;
}

interface UpdateTablePreferencesRequest {
  preferences: Partial<TableUIPreferences>;
}

export class ConfigService {
  #httpClient: HttpClient;

  constructor(httpClient: HttpClient) {
    this.#httpClient = httpClient;
  }

  public async getOrgConfig<T = unknown>(
    orgId: string,
    key: string,
    options?: Partial<Options>,
  ): Promise<T | null> {
    try {
      const requestUrl = this.#httpClient.constructUrl(
        `v1/config/org/${orgId}/key/${encodeURIComponent(key)}`,
      );
      const response = await this.#httpClient.get(requestUrl, {
        signal: options?.abortSignal,
        headers: robotoHeaders({ orgId }),
      });
      const data = await response.json<GetConfigResponse<T>>();
      return data.value;
    } catch (error) {
      // If config value doesn't exist (404), return null
      if (error instanceof RobotoNotFoundException) {
        return null;
      }
      throw error;
    }
  }

  public async setOrgConfig<T = unknown>(
    orgId: string,
    key: string,
    value: T,
    options?: Partial<Options>,
  ): Promise<void> {
    const requestUrl = this.#httpClient.constructUrl(
      `v1/config/org/${orgId}/key/${encodeURIComponent(key)}`,
    );
    const request: SetConfigRequest = { value };

    await this.#httpClient.put(requestUrl, {
      signal: options?.abortSignal,
      headers: robotoHeaders({ orgId }),
      body: JSON.stringify(request),
    });
  }

  public async getUserConfig<T = unknown>(
    key: RobotoUserConfigKey,
    options?: Partial<Options>,
  ): Promise<T | null> {
    try {
      const requestUrl = this.#httpClient.constructUrl(
        `v1/config/user/key/${encodeURIComponent(key)}`,
      );
      const response = await this.#httpClient.get(requestUrl, {
        signal: options?.abortSignal,
      });
      const data = await response.json<GetConfigResponse<T>>();
      return data.value;
    } catch (error) {
      // If config value doesn't exist (404), return null
      if (error instanceof RobotoNotFoundException) {
        return null;
      }
      throw error;
    }
  }

  public async setUserConfig(
    key: RobotoUserConfigKey,
    value: unknown,
    options?: Partial<Options>,
  ): Promise<void> {
    const requestUrl = this.#httpClient.constructUrl(
      `v1/config/user/key/${encodeURIComponent(key)}`,
    );
    const request: SetConfigRequest = { value };

    await this.#httpClient.put(requestUrl, {
      signal: options?.abortSignal,
      body: JSON.stringify(request),
    });
  }

  public async updateTablePreferences(
    orgId: string,
    configKey: RobotoUserConfigKey,
    preferences: Partial<TableUIPreferences>,
    options?: Partial<Options>,
  ): Promise<TableSearchPreferencesConfig> {
    const requestUrl = this.#httpClient.constructUrl(
      `v1/config/user/org/${orgId}/key/${configKey}`,
    );

    const request: UpdateTablePreferencesRequest = {
      preferences,
    };

    const response = await this.#httpClient.put(requestUrl, {
      signal: options?.abortSignal,
      body: JSON.stringify(request),
    });

    const data =
      await response.json<GetConfigResponse<TableSearchPreferencesConfig>>();
    return data.value;
  }
}
